import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  api,
  requestLibraryUploadUrl,
  uploadFileToS3,
  createLibraryDocument,
  parseDuplicateNameConflict,
  type DuplicateLibraryDocument,
} from '@/lib/api'
import { validateFile, isDocumentInFlight } from '@/lib/schemas'
import { mapWithConcurrency, MAX_PARALLEL_UPLOADS } from '@/lib/map-with-concurrency'
import { getExpiryStatus } from '@/lib/document-expiry'
import { appendIfAbsentById } from '@/lib/utils'
import type {
  LibraryFolder,
  LibraryDocument,
  DocumentTag,
  DocumentCategory,
  LibraryFolderCategory,
  DocumentVersionHistory,
  ProcessingStatus,
} from '@/lib/schemas'

// ===================
// Upload Types
// ===================

export interface UploadingFile {
  id: string
  file: File
  progress: number
  status: 'pending' | 'uploading' | 'complete' | 'error'
  error?: string
  folderId: string | null
}

/**
 * A file whose upload was stopped because a live document of the same name
 * already exists in the target folder (F4). Held until the user chooses to
 * supersede the existing document or to keep both copies — never uploaded
 * silently, because two live copies of one document both feed AI retrieval.
 */
export interface DuplicateUpload {
  id: string
  file: File
  folderId: string | null
  existing: DuplicateLibraryDocument
}

// ===================
// Store State Types
// ===================

interface LibraryState {
  // Folder State
  folders: LibraryFolder[]
  selectedFolderId: string | null
  expandedFolderIds: Set<string>

  // Document State
  documents: LibraryDocument[]
  selectedDocumentIds: Set<string>
  // Pagination — the list is server-paginated (default 100 per page). `total` is
  // the full count matching the current server-side filters; `documents.length`
  // is how much of it is loaded. A "Load more" affordance shows while the loaded
  // window is short of `total`. `documentsScope` records the folderId the loaded
  // window was fetched for, so loadMoreDocuments pages the same query.
  documentsTotal: number
  isLoadingMore: boolean
  documentsScope: string | null | undefined

  // Tags State
  tags: DocumentTag[]

  // UI State
  isLoading: boolean
  error: string | null
  searchQuery: string
  /** True from the keystroke until the debounced server search for that term
   *  lands. Search is server-side with no client-side fallback filter, so during
   *  this window the loaded rows still answer the PREVIOUS term — the list must
   *  not present them as results for what the user just typed. */
  isSearchPending: boolean

  // Filter State — applied by getFilteredDocuments.
  categoryFilter: Set<DocumentCategory>
  // Tag filter — applied at the API level (?tags=) so retrieval is consistent
  // with what the user sees. OR semantics: doc matches if it has any of these tags.
  selectedTagIds: Set<string>

  // Upload State
  uploadingFiles: UploadingFile[]
  // Same-name collisions awaiting a user decision (F4). Resolved one at a time.
  duplicateUploads: DuplicateUpload[]

  // Polling State
  pollingIntervalId: ReturnType<typeof setInterval> | null

  // Upload Actions
  uploadLibraryDocuments: (
    folderId: string | null,
    files: File[],
    options?: { allowDuplicate?: boolean }
  ) => Promise<void>
  removeUploadingFile: (id: string) => void

  // Duplicate-upload resolution (F4)
  /** Supersede the colliding document — routes into the existing replace flow. */
  resolveDuplicateAsNewVersion: (id: string) => Promise<void>
  /** Re-run the upload with an explicit opt-in to a second live copy. */
  resolveDuplicateKeepBoth: (id: string) => Promise<void>
  /** Drop the pending file without uploading it. */
  dismissDuplicateUpload: (id: string) => void

  // Folder Actions
  fetchFolders: () => Promise<void>
  createFolder: (name: string, parentId: string | null, category: LibraryFolderCategory) => Promise<LibraryFolder>
  updateFolder: (id: string, updates: Partial<Pick<LibraryFolder, 'name' | 'category' | 'parentId'>>) => Promise<void>
  deleteFolder: (id: string, targetFolderId?: string) => Promise<void>
  selectFolder: (folderId: string | null) => void
  toggleFolderExpanded: (folderId: string) => void

  // Document Actions
  fetchDocuments: (folderId?: string | null) => Promise<void>
  /** Append the next page of documents to the loaded window. No-op when the
   *  window already covers `documentsTotal` or a load is in flight. */
  loadMoreDocuments: () => Promise<void>
  addDocument: (document: Omit<LibraryDocument, 'id' | 'createdAt'>) => Promise<LibraryDocument>
  moveDocument: (documentId: string, folderId: string | null) => Promise<void>
  bulkMoveDocuments: (documentIds: string[], folderId: string | null) => Promise<void>
  deleteDocument: (documentId: string) => Promise<void>
  bulkDeleteDocuments: (documentIds: string[]) => Promise<void>
  selectDocument: (documentId: string, selected: boolean) => void
  selectAllDocuments: (selected: boolean) => void
  clearDocumentSelection: () => void

  // Tag Actions
  fetchTags: () => Promise<void>
  createTag: (name: string, color?: string) => Promise<DocumentTag>
  deleteTag: (tagId: string) => Promise<void>
  addTagToDocument: (documentId: string, tagId: string) => Promise<void>
  removeTagFromDocument: (documentId: string, tagId: string) => Promise<void>

  // Quality tier actions (PR 7) — 'gold' retired, archive only
  setQualityTier: (documentId: string, tier: 'archive' | null) => Promise<void>

  // Fact Bank (Layer 1)
  setFactBank: (documentId: string, inFactBank: boolean) => Promise<void>
  setFactBankBulk: (documentIds: string[], inFactBank: boolean) => Promise<void>
  factBankFilter: boolean
  setFactBankFilter: (on: boolean) => void

  // Expiring lens (library E2E finding) — client-side filter to the "attention
  // set": documents that are expiring-soon OR already expired. Triggered from
  // the overview's "Expiring soon" line. Unlike factBankFilter this is applied
  // entirely client-side in getFilteredDocuments (no server param).
  expiringFilter: boolean
  setExpiringFilter: (on: boolean) => void

  // Hide-archived toggle (PR 7)
  hideArchived: boolean
  setHideArchived: (hide: boolean) => void

  // Download Actions
  getDownloadUrl: (documentId: string) => Promise<string>

  // Rename Actions
  renameDocument: (documentId: string, name: string) => Promise<void>

  // Versioning Actions (library versioning)
  /** Replace a document's file with a new version: presign → S3 PUT → commit
   *  (optimistic-concurrency guarded by `expectedVersion`). Optionally applies
   *  effective/expiry dates in a follow-up PATCH, then refreshes + resumes
   *  polling. A version conflict rejects with the server's "refresh and retry"
   *  message so callers can surface it. */
  replaceDocument: (
    documentId: string,
    file: File,
    expectedVersion: number,
    dates?: { effectiveDate?: string | null; expiryDate?: string | null }
  ) => Promise<void>
  updateDocumentDates: (
    documentId: string,
    dates: { effectiveDate?: string | null; expiryDate?: string | null }
  ) => Promise<void>
  /** Presigned download URL for a superseded version. Omit `version` for the
   *  most recently superseded one. */
  getPreviousVersionUrl: (documentId: string, version?: number) => Promise<string>
  /** Retained version history, newest first (F26). Not cached in the store —
   *  it's read on demand when the history dialog opens. */
  fetchDocumentVersions: (documentId: string) => Promise<DocumentVersionHistory>

  // Overview Actions
  fetchAllDocuments: () => Promise<void>
  getRecentDocuments: (limit?: number) => LibraryDocument[]

  // Filter Actions
  setSearchQuery: (query: string) => void
  toggleCategoryFilter: (category: DocumentCategory) => void
  toggleTagFilter: (tagId: string) => void
  clearTagFilter: () => void
  clearAllFilters: () => void

  // Polling Actions
  startStatusPolling: () => void
  stopStatusPolling: () => void

  // Utility
  clearError: () => void
  getFolderPath: (folderId: string) => LibraryFolder[]
  getChildFolders: (parentId: string | null) => LibraryFolder[]
  getRootFolders: () => LibraryFolder[]
  getDocumentsInFolder: (folderId: string | null) => LibraryDocument[]
  /** Like getDocumentsInFolder but also applies category filter. */
  getFilteredDocuments: (folderId: string | null) => LibraryDocument[]
  /** True when any filter is active: category, tags, or the Fact Bank lens. */
  hasActiveFilters: () => boolean
}

// ===================
// API Response Types
// ===================

interface FolderWithDocumentCount extends LibraryFolder {
  documentCount: number
}

interface ListDocumentsResponse {
  documents: LibraryDocument[]
  total: number
  limit: number
  offset: number
}

// Server default page size (matches the library handler cap logic).
const DOCUMENTS_PAGE_SIZE = 100

// Debounce for server-side search-as-you-type refetches.
const SEARCH_DEBOUNCE_MS = 300
let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

// Monotonic search identity, so only the newest term's fetch may clear
// isSearchPending (a superseded fetch settling late must not un-dim the list).
let searchPendingSeq = 0

// Monotonic fetch identity: a slow page-1 response must not clobber the window
// a newer fetch (folder switch, search change) already installed.
let documentsFetchSeq = 0

/**
 * Apply a Fact Bank PATCH response to a loaded document. The shield tooltip
 * reads the curator and the time, so both come from the response, not just
 * the flag — otherwise a fresh add reads "In Fact Bank" with no curator until
 * the next refetch. The response row carries no curator name (list responses
 * join it in), so a loaded name is kept only while the curator is unchanged.
 */
function withFactBankFrom(doc: LibraryDocument, updated: LibraryDocument): LibraryDocument {
  const factBankSetBy = updated.factBankSetBy ?? null
  return {
    ...doc,
    inFactBank: updated.inFactBank ?? false,
    factBankSetBy,
    factBankSetAt: updated.factBankSetAt ?? null,
    factBankSetByName:
      updated.factBankSetByName ??
      (factBankSetBy === doc.factBankSetBy ? (doc.factBankSetByName ?? null) : null),
  }
}

/**
 * Build the shared /library/documents query params (folder + tag + archive +
 * fact-bank + search filters). Used by both the initial fetch and load-more so
 * paging stays on the same query. Pagination (limit/offset) is added by the
 * caller.
 */
function buildDocumentListParams(
  state: Pick<LibraryState, 'selectedTagIds' | 'hideArchived' | 'factBankFilter' | 'searchQuery'>,
  folderId: string | null | undefined
): Record<string, string> {
  const params: Record<string, string> = {}
  if (folderId !== null && folderId !== undefined) {
    params['folderId'] = folderId
  }
  const tagIds = Array.from(state.selectedTagIds)
  if (tagIds.length > 0) {
    params['tags'] = tagIds.join(',')
  }
  if (!state.hideArchived) {
    params['include_archived'] = 'true'
  }
  if (state.factBankFilter) {
    params['fact_bank'] = 'true'
  }
  const search = state.searchQuery.trim()
  if (search) {
    params['search'] = search
  }
  return params
}

// ===================
// Store Implementation
// ===================

export const useLibraryStore = create<LibraryState>()(
  devtools(
    (set, get) => ({
      // Initial State
      folders: [],
      selectedFolderId: null,
      expandedFolderIds: new Set<string>(),
      documents: [],
      selectedDocumentIds: new Set<string>(),
      documentsTotal: 0,
      isLoadingMore: false,
      documentsScope: undefined,
      tags: [],
      isLoading: false,
      error: null,
      searchQuery: '',
      isSearchPending: false,
      categoryFilter: new Set<DocumentCategory>(),
      selectedTagIds: new Set<string>(),
      hideArchived: true,  // PR 7: default ON so archived docs don't clutter the list
      factBankFilter: false,  // Layer 1: Fact Bank lens off by default
      expiringFilter: false,  // Expiring lens off by default (client-side)
      uploadingFiles: [],
      duplicateUploads: [],
      pollingIntervalId: null,

      // Upload Actions
      uploadLibraryDocuments: async (folderId, files, options) => {
        // Validate files and create upload entries
        const newUploadingFiles: UploadingFile[] = []

        for (const file of files) {
          const validation = validateFile(file)
          if (validation.valid) {
            newUploadingFiles.push({
              id: crypto.randomUUID(),
              file,
              progress: 0,
              status: 'pending',
              folderId,
            })
          }
        }

        if (newUploadingFiles.length === 0) return

        // Add to uploadingFiles state
        set((state) => ({
          uploadingFiles: [...state.uploadingFiles, ...newUploadingFiles],
        }))

        const runUpload = async (uploadingFile: UploadingFile) => {
          try {
            // Update status to uploading
            set((state) => ({
              uploadingFiles: state.uploadingFiles.map((f) =>
                f.id === uploadingFile.id ? { ...f, status: 'uploading' as const } : f
              ),
            }))

            // Step 1: Get presigned URL
            const { documentId, uploadUrl, s3Key } = await requestLibraryUploadUrl(
              uploadingFile.file.name,
              uploadingFile.file.type,
              uploadingFile.file.size,
              folderId,
              options?.allowDuplicate
            )

            // Step 2: Upload to S3 with progress tracking
            await uploadFileToS3(uploadUrl, uploadingFile.file, (progress) => {
              set((state) => ({
                uploadingFiles: state.uploadingFiles.map((f) =>
                  f.id === uploadingFile.id ? { ...f, progress } : f
                ),
              }))
            })

            // Step 3: Create document record
            const response = await createLibraryDocument({
              documentId,
              name: uploadingFile.file.name,
              mimeType: uploadingFile.file.type,
              size: uploadingFile.file.size,
              s3Key,
              folderId,
              ...(options?.allowDuplicate ? { allowDuplicate: true } : {}),
            })

            // Map response to LibraryDocument with defaults for missing fields
            const newDocument: LibraryDocument = {
              id: response.id,
              name: response.name,
              type: response.type as DocumentCategory,
              mimeType: response.mimeType,
              size: response.size,
              s3Key: response.s3Key,
              organizationId: response.organizationId,
              uploadedBy: response.uploadedBy,
              folderId: response.folderId,
              processingStatus: response.processingStatus as ProcessingStatus,
              createdAt: response.createdAt,
              ownerId: null,
              tags: [],
            }

            // Add new document to state optimistically AND remove from
            // uploadingFiles. Merged by id, not appended: a status poll or a
            // concurrent fetchDocuments may already have listed this row.
            set((state) => ({
              documents: appendIfAbsentById(state.documents, newDocument),
              uploadingFiles: state.uploadingFiles.filter((f) => f.id !== uploadingFile.id),
            }))
          } catch (error) {
            // F4 — a same-name collision is a decision, not a failure. Park the
            // file in the duplicate queue so the user picks "new version" or
            // "keep both"; never fall through to a silent second live copy.
            const existing = parseDuplicateNameConflict(error)
            if (existing) {
              set((state) => ({
                uploadingFiles: state.uploadingFiles.filter((f) => f.id !== uploadingFile.id),
                duplicateUploads: [
                  ...state.duplicateUploads,
                  { id: uploadingFile.id, file: uploadingFile.file, folderId, existing },
                ],
              }))
              return
            }

            // Mark as error
            const message = error instanceof Error ? error.message : 'Upload failed'
            set((state) => ({
              uploadingFiles: state.uploadingFiles.map((f) =>
                f.id === uploadingFile.id
                  ? { ...f, status: 'error' as const, error: message }
                  : f
              ),
            }))
          }
        }

        // F4 — files sharing a name must not race each other. Run each same-name
        // group in series so the second entry presigns only after the first has
        // been created, and therefore hits the server's collision check instead
        // of slipping past it; distinct names still upload in parallel. Without
        // this, a multi-select or drag-drop of two identically-named files
        // reproduces the original defect entirely client-side.
        const sameNameGroups = new Map<string, UploadingFile[]>()
        for (const uploadingFile of newUploadingFiles) {
          const key = uploadingFile.file.name.toLowerCase()
          const group = sameNameGroups.get(key)
          if (group) {
            group.push(uploadingFile)
          } else {
            sameNameGroups.set(key, [uploadingFile])
          }
        }

        // Groups run a few at a time (see MAX_PARALLEL_UPLOADS), so a 100-file
        // ZIP never has more than that many uploads in flight.
        await mapWithConcurrency(
          Array.from(sameNameGroups.values()),
          MAX_PARALLEL_UPLOADS,
          async (group) => {
            for (const uploadingFile of group) {
              await runUpload(uploadingFile)
            }
          }
        )

        // Refresh documents and start polling. Folder counts are computed
        // server-side, so the tree goes stale unless folders are refetched too.
        await get().fetchDocuments()
        await get().fetchFolders()
        get().startStatusPolling()
      },

      removeUploadingFile: (id) => {
        set((state) => ({
          uploadingFiles: state.uploadingFiles.filter((f) => f.id !== id),
        }))
      },

      // Duplicate-upload resolution (F4)
      resolveDuplicateAsNewVersion: async (id) => {
        const pending = get().duplicateUploads.find((d) => d.id === id)
        if (!pending) return

        // Reuse the versioning path wholesale: it purges the superseded chunks
        // and re-indexes, so exactly one copy stays retrievable. Since F25 this
        // no longer 403s for a document someone else uploaded — any writing
        // member may supersede. Remaining failures are a version conflict (409,
        // someone replaced it first) or a mid-processing document (400); both
        // propagate to the dialog and the entry stays queued so "Keep both" is
        // still reachable.
        await get().replaceDocument(pending.existing.id, pending.file, pending.existing.version)

        set((state) => ({
          duplicateUploads: state.duplicateUploads.filter((d) => d.id !== id),
        }))
      },

      resolveDuplicateKeepBoth: async (id) => {
        const pending = get().duplicateUploads.find((d) => d.id === id)
        if (!pending) return

        set((state) => ({
          duplicateUploads: state.duplicateUploads.filter((d) => d.id !== id),
        }))

        await get().uploadLibraryDocuments(pending.folderId, [pending.file], {
          allowDuplicate: true,
        })
      },

      dismissDuplicateUpload: (id) => {
        set((state) => ({
          duplicateUploads: state.duplicateUploads.filter((d) => d.id !== id),
        }))
      },

      // Folder Actions
      fetchFolders: async () => {
        set({ isLoading: true, error: null })
        try {
          // Send the active server-side filters (tags / archived / fact-bank) so
          // the per-folder counts match the document list. `undefined` folderId
          // omits the folderId param — counts span the whole library, grouped by
          // folder server-side (independent of the paged document window).
          const params = buildDocumentListParams(get(), undefined)
          const folders = await api.get<FolderWithDocumentCount[]>('/library/folders', params)
          set({ folders, isLoading: false })
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to fetch folders'
          set({ error: message, isLoading: false })
        }
      },

      createFolder: async (name, parentId, category) => {
        set({ isLoading: true, error: null })
        try {
          const newFolder = await api.post<LibraryFolder>('/library/folders', {
            name,
            parentId,
            category,
          })
          set((state) => ({
            folders: [...state.folders, { ...newFolder, documentCount: 0 }],
            isLoading: false,
          }))
          return newFolder
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create folder'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      updateFolder: async (id, updates) => {
        set({ isLoading: true, error: null })
        try {
          const updatedFolder = await api.patch<LibraryFolder>(`/library/folders/${id}`, updates)
          set((state) => ({
            folders: state.folders.map((folder) =>
              folder.id === id ? { ...folder, ...updatedFolder } : folder
            ),
            isLoading: false,
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update folder'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      deleteFolder: async (id, targetFolderId) => {
        set({ isLoading: true, error: null })
        try {
          // Collect all folder IDs that will be deleted (including children)
          const folderIdsToDelete = new Set<string>([id])
          const findChildren = (parentId: string) => {
            get().folders.forEach((folder) => {
              if (folder.parentId === parentId) {
                folderIdsToDelete.add(folder.id)
                findChildren(folder.id)
              }
            })
          }
          findChildren(id)

          // If a target folder is specified, move affected documents there first
          if (targetFolderId) {
            const affectedDocIds = get()
              .documents.filter((doc) => doc.folderId && folderIdsToDelete.has(doc.folderId))
              .map((doc) => doc.id)
            if (affectedDocIds.length > 0) {
              await api.post('/library/documents/bulk-move', {
                documentIds: affectedDocIds,
                folderId: targetFolderId,
              })
            }
          }

          await api.delete(`/library/folders/${id}`)

          set((state) => ({
            folders: state.folders.filter((f) => !folderIdsToDelete.has(f.id)),
            documents: state.documents.map((doc) =>
              doc.folderId && folderIdsToDelete.has(doc.folderId)
                ? { ...doc, folderId: targetFolderId ?? null }
                : doc
            ),
            selectedFolderId:
              state.selectedFolderId && folderIdsToDelete.has(state.selectedFolderId)
                ? null
                : state.selectedFolderId,
            isLoading: false,
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete folder'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      selectFolder: (folderId) => {
        set({ selectedFolderId: folderId, selectedDocumentIds: new Set() })
        // Fetch the folder's own first page. The loaded window is one page of
        // the org's documents, so a folder whose documents fall beyond it would
        // otherwise render incomplete (and "Load more" paged the whole org, not
        // the folder).
        void get().fetchDocuments(folderId)
      },

      toggleFolderExpanded: (folderId) => {
        set((state) => {
          const newExpanded = new Set(state.expandedFolderIds)
          if (newExpanded.has(folderId)) {
            newExpanded.delete(folderId)
          } else {
            newExpanded.add(folderId)
          }
          return { expandedFolderIds: newExpanded }
        })
      },

      // Document Actions
      fetchDocuments: async (folderId) => {
        const fetchSeq = ++documentsFetchSeq
        set({ isLoading: true, error: null })
        try {
          // Page 1 of the current filter set. folderId null/undefined = fetch ALL
          // documents (for "All Documents" view). Tag / archive / fact-bank
          // filters are applied server-side so retrieval matches what's shown.
          const params: Record<string, string> = {
            ...buildDocumentListParams(get(), folderId),
            limit: String(DOCUMENTS_PAGE_SIZE),
            offset: '0',
          }
          const response = await api.get<ListDocumentsResponse>('/library/documents', params)

          // A newer fetch (folder switch, search change) superseded this one —
          // installing this stale window would show the wrong folder/results.
          if (fetchSeq !== documentsFetchSeq) return

          // Clean stale tag IDs from the filter set (if a tag was deleted by
          // another user, drop it silently rather than confusing the user with
          // an empty result set). Only runs when we have a tags list to
          // compare against — otherwise the first call (before fetchTags) would
          // strip everything.
          const currentTags = get().tags
          let cleanedTagIds = get().selectedTagIds
          if (currentTags.length > 0 && cleanedTagIds.size > 0) {
            const knownTagIds = new Set(currentTags.map((t) => t.id))
            cleanedTagIds = new Set(
              Array.from(cleanedTagIds).filter((id) => knownTagIds.has(id))
            )
          }
          set({
            documents: response.documents ?? [],
            documentsTotal: response.total ?? (response.documents?.length ?? 0),
            documentsScope: folderId,
            isLoading: false,
            selectedTagIds: cleanedTagIds,
          })
        } catch (error) {
          if (fetchSeq !== documentsFetchSeq) return
          const message = error instanceof Error ? error.message : 'Failed to fetch documents'
          set({ error: message, isLoading: false })
        }
      },

      loadMoreDocuments: async () => {
        const { documents, documentsTotal, isLoadingMore, documentsScope } = get()
        if (isLoadingMore || documents.length >= documentsTotal) return

        const seqAtStart = documentsFetchSeq
        set({ isLoadingMore: true })
        try {
          const params: Record<string, string> = {
            ...buildDocumentListParams(get(), documentsScope),
            limit: String(DOCUMENTS_PAGE_SIZE),
            offset: String(documents.length),
          }
          const response = await api.get<ListDocumentsResponse>('/library/documents', params)

          // A fresh page-1 fetch replaced the window mid-flight — appending this
          // page would mix two different scopes.
          if (seqAtStart !== documentsFetchSeq) {
            set({ isLoadingMore: false })
            return
          }

          // Append, de-duping by id so a concurrent insert can't double a row.
          const existingIds = new Set(get().documents.map((d) => d.id))
          const appended = (response.documents ?? []).filter((d) => !existingIds.has(d.id))
          set((state) => ({
            documents: [...state.documents, ...appended],
            documentsTotal: response.total ?? state.documentsTotal,
            isLoadingMore: false,
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to load more documents'
          set({ error: message, isLoadingMore: false })
        }
      },

      addDocument: async (document) => {
        // This is called after successful upload, just refreshes the document list
        // The actual upload is handled by the upload components
        set({ isLoading: true, error: null })
        try {
          // Refresh documents for the current folder
          await get().fetchDocuments(get().selectedFolderId)
          set({ isLoading: false })
          // Return the document (frontend provides it after creation)
          return document as LibraryDocument
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to add document'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      moveDocument: async (documentId, folderId) => {
        set({ isLoading: true, error: null })
        try {
          await api.patch(`/library/documents/${documentId}/folder`, { folderId })
          set((state) => ({
            documents: state.documents.map((doc) =>
              doc.id === documentId ? { ...doc, folderId } : doc
            ),
            isLoading: false,
          }))
          // A move changes two folder counts — source and destination.
          void get().fetchFolders()
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to move document'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      bulkMoveDocuments: async (documentIds, folderId) => {
        set({ isLoading: true, error: null })
        try {
          await api.post('/library/documents/bulk-move', { documentIds, folderId })
          const idsSet = new Set(documentIds)
          set((state) => ({
            documents: state.documents.map((doc) =>
              idsSet.has(doc.id) ? { ...doc, folderId } : doc
            ),
            selectedDocumentIds: new Set(),
            isLoading: false,
          }))
          void get().fetchFolders()
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to move documents'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      deleteDocument: async (documentId) => {
        set({ isLoading: true, error: null })
        try {
          await api.delete(`/library/documents/${documentId}`)
          set((state) => ({
            documents: state.documents.filter((doc) => doc.id !== documentId),
            selectedDocumentIds: (() => {
              const newSet = new Set(state.selectedDocumentIds)
              newSet.delete(documentId)
              return newSet
            })(),
            isLoading: false,
          }))
          void get().fetchFolders()
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete document'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      bulkDeleteDocuments: async (documentIds) => {
        if (documentIds.length === 0) return

        set({ isLoading: true, error: null })
        try {
          // Delete documents in parallel
          await Promise.all(
            documentIds.map((id) => api.delete(`/library/documents/${id}`))
          )
          const idsSet = new Set(documentIds)
          set((state) => ({
            documents: state.documents.filter((doc) => !idsSet.has(doc.id)),
            selectedDocumentIds: new Set(),
            isLoading: false,
          }))
          void get().fetchFolders()
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete documents'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      selectDocument: (documentId, selected) => {
        set((state) => {
          const newSet = new Set(state.selectedDocumentIds)
          if (selected) {
            newSet.add(documentId)
          } else {
            newSet.delete(documentId)
          }
          return { selectedDocumentIds: newSet }
        })
      },

      selectAllDocuments: (selected) => {
        set((state) => {
          if (selected) {
            const currentDocs = get().getDocumentsInFolder(state.selectedFolderId)
            return { selectedDocumentIds: new Set(currentDocs.map((d) => d.id)) }
          }
          return { selectedDocumentIds: new Set() }
        })
      },

      clearDocumentSelection: () => {
        set({ selectedDocumentIds: new Set() })
      },

      // Tag Actions
      fetchTags: async () => {
        try {
          const tags = await api.get<DocumentTag[]>('/library/tags')
          set({ tags })
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to fetch tags'
          set({ error: message })
        }
      },

      createTag: async (name, color) => {
        try {
          const newTag = await api.post<DocumentTag>('/library/tags', { name, color })
          set((state) => ({ tags: [...state.tags, newTag] }))
          return newTag
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to create tag'
          set({ error: message })
          throw error
        }
      },

      deleteTag: async (tagId) => {
        try {
          await api.delete(`/library/tags/${tagId}`)
          set((state) => ({
            tags: state.tags.filter((t) => t.id !== tagId),
            documents: (state.documents ?? []).map((doc) => ({
              ...doc,
              tags: (doc.tags ?? []).filter((t) => t.id !== tagId),
            })),
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to delete tag'
          set({ error: message })
          throw error
        }
      },

      addTagToDocument: async (documentId, tagId) => {
        try {
          await api.post(`/library/documents/${documentId}/tags`, { tagId })
          const tag = get().tags.find((t) => t.id === tagId)
          if (!tag) throw new Error('Tag not found')

          set((state) => ({
            documents: (state.documents ?? []).map((doc) =>
              doc.id === documentId && !(doc.tags ?? []).some((t) => t.id === tagId)
                ? { ...doc, tags: [...(doc.tags ?? []), tag] }
                : doc
            ),
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to add tag'
          set({ error: message })
          throw error
        }
      },

      removeTagFromDocument: async (documentId, tagId) => {
        try {
          await api.delete(`/library/documents/${documentId}/tags/${tagId}`)
          set((state) => ({
            documents: (state.documents ?? []).map((doc) =>
              doc.id === documentId
                ? { ...doc, tags: (doc.tags ?? []).filter((t) => t.id !== tagId) }
                : doc
            ),
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to remove tag'
          set({ error: message })
          throw error
        }
      },

      // Quality tier (PR 7)
      setQualityTier: async (documentId, tier) => {
        try {
          const updated = await api.patch<LibraryDocument>(
            `/library/documents/${documentId}/quality-tier`,
            { tier }
          )
          set((state) => ({
            documents: state.documents.map((d) =>
              d.id === documentId
                ? { ...d, qualityTier: updated.qualityTier ?? null }
                : d
            ),
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update quality tier'
          set({ error: message })
          throw error
        }
      },

      // Fact Bank membership (Layer 1). Folded into the general document PATCH
      // route ({ inFactBank }) — the SAM template is at the CFN 500-resource
      // limit, so no dedicated /fact-bank endpoint exists.
      setFactBank: async (documentId, inFactBank) => {
        try {
          const updated = await api.patch<LibraryDocument>(
            `/library/documents/${documentId}`,
            { inFactBank }
          )
          set((state) => ({
            // While the Fact Bank lens is on, a removed doc no longer matches
            // the active filter — drop it instead of leaving a stale row.
            documents:
              state.factBankFilter && !inFactBank
                ? state.documents.filter((d) => d.id !== documentId)
                : state.documents.map((d) =>
                    d.id === documentId ? withFactBankFrom(d, updated) : d
                  ),
          }))
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to update Fact Bank'
          set({ error: message })
          throw error
        }
      },

      // Bulk membership toggle — no bulk endpoint exists (CFN 500-resource
      // limit), so PATCH per document and apply whichever succeed.
      setFactBankBulk: async (documentIds, inFactBank) => {
        const results = await Promise.allSettled(
          documentIds.map((id) =>
            api.patch<LibraryDocument>(`/library/documents/${id}`, { inFactBank })
          )
        )
        const updatedById = new Map<string, LibraryDocument>()
        results.forEach((result, i) => {
          const id = documentIds[i]
          if (result.status === 'fulfilled' && id !== undefined) {
            updatedById.set(id, result.value)
          }
        })
        set((state) => ({
          documents:
            state.factBankFilter && !inFactBank
              ? state.documents.filter((d) => !updatedById.has(d.id))
              : state.documents.map((d) => {
                  const updated = updatedById.get(d.id)
                  return updated ? withFactBankFrom(d, updated) : d
                }),
        }))
        const failedCount = results.filter((r) => r.status === 'rejected').length
        if (failedCount > 0) {
          const message = `Failed to update Fact Bank for ${failedCount} document${failedCount === 1 ? '' : 's'}`
          set({ error: message })
          throw new Error(message)
        }
      },

      setFactBankFilter: (on) => {
        set({ factBankFilter: on })
        void get().fetchDocuments(get().selectedFolderId)
        // Re-fetch folders so the per-folder counts reflect the fact-bank lens.
        void get().fetchFolders()
      },

      // Client-side only — no server param, so no re-fetch. getFilteredDocuments
      // narrows the already-loaded window to the expiry attention set.
      setExpiringFilter: (on) => {
        set({ expiringFilter: on })
      },

      // Hide-archived toggle (PR 7)
      setHideArchived: (hide) => {
        set({ hideArchived: hide })
        void get().fetchDocuments(get().selectedFolderId)
        void get().fetchFolders()
      },

      // Download Actions
      getDownloadUrl: async (documentId) => {
        const response = await api.get<{ url: string }>(`/library/documents/${documentId}/download-url`)
        return response.url
      },

      // Rename Actions
      renameDocument: async (documentId, name) => {
        const response = await api.patch<LibraryDocument>(`/library/documents/${documentId}`, { name })
        set((state) => ({
          documents: state.documents.map((d) =>
            d.id === documentId ? { ...d, name: response.name } : d
          ),
        }))
      },

      // Versioning Actions (library versioning)
      replaceDocument: async (documentId, file, expectedVersion, dates) => {
        // Step 1: presigned URL for the new version's bytes.
        const { uploadUrl, s3Key } = await api.post<{
          uploadUrl: string
          s3Key: string
          nextVersion: number
          expiresIn: number
        }>(`/library/documents/${documentId}/replace-url`, {
          filename: file.name,
          mimeType: file.type,
          size: file.size,
        })

        // Step 2: PUT the bytes straight to S3 (raw fetch — not an API route).
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        })
        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status ${uploadResponse.status}`)
        }

        // Step 3: commit the new version. A 409 propagates with the server's
        // "refresh and retry" copy so the dialog can show it.
        const updated = await api.post<LibraryDocument>(
          `/library/documents/${documentId}/replace`,
          {
            s3Key,
            expectedVersion,
            name: file.name,
            mimeType: file.type,
            size: file.size,
          }
        )

        // Step 4: dates can't ride the replace payload — apply them separately.
        if (dates) {
          await get().updateDocumentDates(documentId, dates)
        }

        // Optimistic merge: the replaced doc re-enters processing.
        set((state) => ({
          documents: state.documents.map((d) =>
            d.id === documentId
              ? { ...d, ...updated, processingStatus: 'pending' as ProcessingStatus }
              : d
          ),
        }))

        // Refresh from server + resume polling until processing completes.
        await get().fetchDocuments()
        get().startStatusPolling()
      },

      updateDocumentDates: async (documentId, dates) => {
        const updated = await api.patch<LibraryDocument>(
          `/library/documents/${documentId}`,
          { effectiveDate: dates.effectiveDate, expiryDate: dates.expiryDate }
        )
        set((state) => ({
          documents: state.documents.map((d) =>
            d.id === documentId ? { ...d, ...updated } : d
          ),
        }))
      },

      getPreviousVersionUrl: async (documentId, version) => {
        const query = version !== undefined ? `?version=${version}` : ''
        const response = await api.get<{
          downloadUrl: string
          version: number
          name: string
          expiresIn: number
        }>(`/library/documents/${documentId}/previous-version-url${query}`)
        return response.downloadUrl
      },

      fetchDocumentVersions: async (documentId) => {
        return api.get<DocumentVersionHistory>(`/library/documents/${documentId}/versions`)
      },

      // Overview Actions
      fetchAllDocuments: async () => {
        set({ isLoading: true, error: null })
        try {
          const response = await api.get<ListDocumentsResponse>('/library/documents')
          set({ documents: response.documents ?? [], isLoading: false })
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Failed to fetch documents'
          set({ error: message, isLoading: false })
        }
      },

      getRecentDocuments: (limit = 10) => {
        const { documents } = get()
        return [...documents]
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .slice(0, limit)
      },

      // Filter Actions
      setSearchQuery: (query) => {
        // isSearchPending goes up on the KEYSTROKE, not when the request leaves:
        // the debounce window is most of the gap in which the rows on screen
        // still answer the old term, and there is no client-side filter left to
        // paper over it.
        const pendingSeq = ++searchPendingSeq
        set({ searchQuery: query, isSearchPending: true })
        // Server-side search (debounced): the loaded window is one page, so a
        // client-only filter silently misses matches beyond it. Search spans
        // all folders (matching the panel's "results across all folders"
        // presentation); clearing restores the selected folder's page.
        if (searchDebounceTimer !== null) clearTimeout(searchDebounceTimer)
        searchDebounceTimer = setTimeout(() => {
          searchDebounceTimer = null
          void get()
            .fetchDocuments(query.trim() ? undefined : get().selectedFolderId)
            .finally(() => {
              // Only the newest term may lower the flag. An older fetch settling
              // late would otherwise un-dim the list while a newer term is still
              // in flight — exactly the stale-rows-as-results bug, one term over.
              if (pendingSeq === searchPendingSeq) {
                set({ isSearchPending: false })
              }
            })
        }, SEARCH_DEBOUNCE_MS)
      },

      toggleCategoryFilter: (category) => {
        set((state) => {
          const next = new Set(state.categoryFilter)
          if (next.has(category)) next.delete(category)
          else next.add(category)
          return { categoryFilter: next }
        })
      },

      toggleTagFilter: (tagId) => {
        set((state) => {
          const next = new Set(state.selectedTagIds)
          if (next.has(tagId)) next.delete(tagId)
          else next.add(tagId)
          return { selectedTagIds: next }
        })
        // Re-fetch with the new tag filter applied server-side
        void get().fetchDocuments(get().selectedFolderId)
        void get().fetchFolders()
      },

      clearTagFilter: () => {
        set({ selectedTagIds: new Set<string>() })
        void get().fetchDocuments(get().selectedFolderId)
        void get().fetchFolders()
      },

      clearAllFilters: () => {
        set({
          categoryFilter: new Set<DocumentCategory>(),
          selectedTagIds: new Set<string>(),
          factBankFilter: false,
          expiringFilter: false,
        })
        void get().fetchDocuments(get().selectedFolderId)
        void get().fetchFolders()
      },

      // Polling Actions
      startStatusPolling: () => {
        const { documents, pollingIntervalId } = get()

        // Don't start if already polling
        if (pollingIntervalId) return

        // Poll until every document reaches a terminal display state — the pill
        // shows one unified Processing through extraction AND indexing, so the
        // poll must live exactly as long as the pill spins.
        const hasPendingDocs = documents.some((doc) => isDocumentInFlight(doc))

        if (!hasPendingDocs) return

        const intervalId = setInterval(async () => {
          const currentDocs = get().documents
          const stillProcessing = currentDocs.some((doc) => isDocumentInFlight(doc))

          if (stillProcessing) {
            // Always fetch ALL documents during polling to catch any pending docs
            // This avoids folder ID mismatch between upload dialog and store
            await get().fetchDocuments()
          } else {
            get().stopStatusPolling()
          }
        }, 3000)

        set({ pollingIntervalId: intervalId })
      },

      stopStatusPolling: () => {
        const { pollingIntervalId } = get()
        if (pollingIntervalId) {
          clearInterval(pollingIntervalId)
          set({ pollingIntervalId: null })
        }
      },

      // Utility
      clearError: () => {
        set({ error: null })
      },

      getFolderPath: (folderId) => {
        const path: LibraryFolder[] = []
        let currentId: string | null = folderId
        const folders = get().folders

        while (currentId) {
          const folder = folders.find((f) => f.id === currentId)
          if (folder) {
            path.unshift(folder)
            currentId = folder.parentId
          } else {
            break
          }
        }

        return path
      },

      getChildFolders: (parentId) => {
        return get().folders.filter((f) => f.parentId === parentId)
      },

      getRootFolders: () => {
        return get().folders.filter((f) => f.parentId === null)
      },

      getDocumentsInFolder: (folderId) => {
        const { documents } = get()
        // When folderId is null, return ALL documents (All Documents view)
        // Otherwise, filter for documents in the specific folder.
        //
        // F27 — no client-side search filter here. Search is server-side (see
        // setSearchQuery) and the window it returns is the answer; re-filtering
        // it on name would throw away every content-only match, which is
        // exactly the class of result the body search exists to find.
        return folderId === null
          ? (documents ?? [])
          : (documents ?? []).filter((doc) => doc.folderId === folderId)
      },

      getFilteredDocuments: (folderId) => {
        const { folders, categoryFilter, expiringFilter } = get()
        // Start from the folder-and-search-filtered list (reuses existing logic)
        let filtered = get().getDocumentsInFolder(folderId)

        // Category: a document's category comes from its folder
        if (categoryFilter.size > 0) {
          const folderById = new Map(folders.map((f) => [f.id, f]))
          filtered = filtered.filter((doc) => {
            const folder = doc.folderId ? folderById.get(doc.folderId) : null
            return folder ? categoryFilter.has(folder.category) : false
          })
        }

        // Expiring lens: the attention set — expiring-soon OR already expired
        // (getExpiryStatus returns non-null for both). Note the overview's
        // "Expiring soon: N" line counts soon-only; the filter deliberately
        // widens to include expired docs so nothing needing renewal is hidden.
        if (expiringFilter) {
          const now = new Date()
          filtered = filtered.filter((doc) => getExpiryStatus(doc, now) !== null)
        }

        return filtered
      },

      hasActiveFilters: () =>
        get().categoryFilter.size > 0 ||
        get().selectedTagIds.size > 0 ||
        get().factBankFilter ||
        get().expiringFilter,
    }),
    { name: 'library-store' }
  )
)
